import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { resumeApi } from "../services/api";
import "../css/cv-preview-modal.css";

interface CvPreviewModalProps {
  open: boolean;
  onClose: () => void;
  language: "pt" | "en";
  downloadUrl: string;
}

const CvPreviewModal: React.FC<CvPreviewModalProps> = ({
  open,
  onClose,
  language,
  downloadUrl,
}) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    const lang = language === "pt" ? "pt-br" : "en-us";
    const blob = await resumeApi.downloadBlob(lang);
    if (blob && blob.size > 0) {
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    }
    setLoading(false);
  }, [language]);

  useEffect(() => {
    if (open) {
      loadPreview();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open, loadPreview]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="cv-modal-overlay" onClick={onClose}>
      <div className="cv-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cv-modal-header">
          <h2 className="cv-modal-title">
            <i className="fa-solid fa-file-pdf" />
            {language === "pt" ? "Currículo" : "Resume"}
          </h2>
          <div className="cv-modal-actions">
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cv-modal-btn cv-modal-btn-download"
            >
              <i className="fa-solid fa-download" />
              <span>{language === "pt" ? "Baixar PDF" : "Download PDF"}</span>
            </a>
            <button className="cv-modal-btn cv-modal-btn-close" onClick={onClose}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        {/* PDF Preview */}
        <div className="cv-modal-body">
          {loading ? (
            <div className="cv-modal-loading">
              <i className="fa-solid fa-spinner fa-spin" />
              <span>{language === "pt" ? "Carregando..." : "Loading..."}</span>
            </div>
          ) : previewUrl ? (
            <div className="cv-modal-pdf-wrapper">
              <iframe
                src={previewUrl}
                title="CV Preview"
                className="cv-modal-iframe"
              />
            </div>
          ) : (
            <div className="cv-modal-loading">
              <i className="fa-solid fa-triangle-exclamation" />
              <span>
                {language === "pt"
                  ? "Não foi possível carregar o currículo."
                  : "Could not load the resume."}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CvPreviewModal;
